import React, { useState, useEffect, useCallback } from 'react';
import AppShell from './components/layout/AppShell';
import CardsTab from './components/tabs/CardsTab';
import FlightsTab from './components/tabs/FlightsTab';
import LoyaltyTab from './components/tabs/LoyaltyTab';
import MoreTab from './components/tabs/MoreTab';
import FlightResultsScreen from './components/screens/FlightResultsScreen';
import PurchaseStrategiesScreen from './components/screens/PurchaseStrategiesScreen';
import TransferReferenceScreen from './components/screens/TransferReferenceScreen';
import NearbyStores from './components/NearbyStores';
import AddCardModal from './components/AddCardModal';
import AuthModal from './components/AuthModal';
import AdminPanel from './components/AdminPanel';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { LoyaltyProvider } from './contexts/LoyaltyContext';
import { storageService, benefitsService } from './services/storage';
import { API_URL } from './config';
import { useTabNavigation, TABS, SUB_SCREENS } from './hooks/useTabNavigation';

function AppContent() {
  const { isAuthenticated, isAdmin, loading: authLoading, logout, getAuthToken, getUserEmail } = useAuth();
  const { activeTab, subScreen, subScreenData, navigateToTab, navigateToSubScreen, goBack } = useTabNavigation();

  const [cards, setCards] = useState([]);
  const [showAddCard, setShowAddCard] = useState(false);
  const [showAuth, setShowAuth] = useState(false);
  const [showAdmin, setShowAdmin] = useState(false);
  const [recommendations, setRecommendations] = useState(null);
  const [loading, setLoading] = useState(false);
  const [merchantData, setMerchantData] = useState(null);
  const [searchHistory, setSearchHistory] = useState([]);
  const [showMigrationPrompt, setShowMigrationPrompt] = useState(false);
  const [showNearbyStores, setShowNearbyStores] = useState(false);

  // ── Benefits state ────────────────────────────────────────────────────────
  const [benefitsData, setBenefitsData] = useState(null);
  const [benefitsLoading, setBenefitsLoading] = useState(false);
  const [benefitsError, setBenefitsError] = useState(null);

  // ── Cards ─────────────────────────────────────────────────────────────────
  const loadCards = useCallback(async () => {
    if (isAuthenticated) {
      const token = await getAuthToken();
      try {
        const cloudCards = await storageService.getCards(token);
        setCards(cloudCards);
        const localCards = storageService.getLocalCards();
        if (localCards.length > 0 && cloudCards.length === 0) {
          setShowMigrationPrompt(true);
        }
      } catch (error) {
        console.error('Failed to load cards from cloud:', error);
        setCards(storageService.getLocalCards());
      }
    } else {
      setCards(storageService.getLocalCards());
    }
  }, [isAuthenticated, getAuthToken]);

  const loadSearchHistory = useCallback(async () => {
    if (isAuthenticated) {
      const token = await getAuthToken();
      if (token) {
        const history = await storageService.getSearchHistory(token);
        setSearchHistory(history);
      }
    } else {
      setSearchHistory([]);
    }
  }, [isAuthenticated, getAuthToken]);

  // ── Benefits ──────────────────────────────────────────────────────────────
  // Use a ref to prevent the effect from re-firing when loading state changes
  const benefitsFetchedRef = React.useRef(false);

  const loadBenefits = useCallback(async (force = false) => {
    if (!isAuthenticated) return;
    const token = await getAuthToken();
    if (!token) return;

    setBenefitsLoading(true);
    setBenefitsError(null);
    try {
      const data = await benefitsService.getCardBenefits(token, force);
      setBenefitsData(data);
    } catch (err) {
      const status = err.status;
      // 404 = endpoint not deployed yet; 502/503 = Lambda unavailable
      const isTransient = !status || status === 404 || status === 502 || status === 503;
      if (isTransient) {
        console.warn('Benefits endpoint unavailable (status', status || 'network', ')');
      } else {
        setBenefitsError('Could not load benefits. Tap refresh to try again.');
      }
    } finally {
      // Always mark as attempted so the effect never re-fires in a loop
      benefitsFetchedRef.current = true;
      setBenefitsLoading(false);
    }
  }, [isAuthenticated, getAuthToken]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Init ──────────────────────────────────────────────────────────────────
  useEffect(() => {
    if (!authLoading) {
      loadCards();
      loadSearchHistory();
    }
  }, [authLoading, loadCards, loadSearchHistory]);

  // Load benefits exactly once when the user is authenticated and has cards.
  // Intentionally omit benefitsLoading/benefitsData from deps to avoid re-fire loops.
  useEffect(() => {
    if (!authLoading && isAuthenticated && cards.length > 0 && !benefitsFetchedRef.current) {
      loadBenefits();
    }
  }, [authLoading, isAuthenticated, cards.length]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Card actions ──────────────────────────────────────────────────────────
  const handleAddCard = async (cardData) => {
    const newCard = {
      id: Date.now().toString(),
      ...cardData,
      addedAt: new Date().toISOString(),
    };
    const token = isAuthenticated ? await getAuthToken() : null;
    await storageService.addCard(newCard, token);
    await loadCards();
    setShowAddCard(false);

    // Prefetch benefits for the new card, then refresh the panel
    if (isAuthenticated && token) {
      benefitsService.prefetchBenefits(token).then(() => {
        benefitsFetchedRef.current = false;
        loadBenefits(true);
      });
    }
  };

  const handleDeleteCard = async (cardId) => {
    const token = isAuthenticated ? await getAuthToken() : null;
    await storageService.deleteCard(cardId, token);
    await loadCards();
    // Refresh benefits so deleted card disappears from the panel
    if (isAuthenticated) { benefitsFetchedRef.current = false; loadBenefits(true); }
  };

  // ── Merchant lookup ───────────────────────────────────────────────────────
  const handleMerchantLookup = async (merchantInfo) => {
    if (cards.length === 0) {
      alert('Please add at least one card to your wallet first');
      return;
    }
    setLoading(true);
    setRecommendations(null);
    setMerchantData(merchantInfo);

    try {
      const token = isAuthenticated ? await getAuthToken() : null;
      const headers = { 'Content-Type': 'application/json' };
      if (token) headers['Authorization'] = `Bearer ${token}`;

      const response = await fetch(`${API_URL}/api/recommend`, {
        method: 'POST',
        headers,
        body: JSON.stringify({ merchant: merchantInfo, cards }),
      });
      if (!response.ok) throw new Error('Failed to fetch recommendations');
      const data = await response.json();
      setRecommendations(data);
      if (isAuthenticated) loadSearchHistory();
    } catch (error) {
      console.error('Error fetching recommendations:', error);
      alert('Error fetching recommendations. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleStoreSelect = (storeData) => {
    setMerchantData(storeData);
    handleMerchantLookup(storeData);
    navigateToTab(TABS.CARDS);
    setShowNearbyStores(false);
  };

  const handleHistoryItemClick = (merchant) => {
    const merchantInfo = { merchantName: merchant };
    setMerchantData(merchantInfo);
    handleMerchantLookup(merchantInfo);
  };

  const handleMigrateCards = async () => {
    try {
      const token = await getAuthToken();
      const result = await storageService.migrateToCloud(token);
      alert(`Successfully synced ${result.migrated} card(s) to your account!`);
      setShowMigrationPrompt(false);
      await loadCards();
    } catch (error) {
      console.error('Migration failed:', error);
      alert('Failed to sync cards. Please try again.');
    }
  };

  const handleLogout = async () => {
    await logout();
    setSearchHistory([]);
    setBenefitsData(null);
    setBenefitsError(null);
    benefitsFetchedRef.current = false;
    loadCards();
  };

  const handleAuthSuccess = () => {
    loadCards();
    loadSearchHistory();
  };

  // ── Render ────────────────────────────────────────────────────────────────
  const renderContent = () => {
    if (subScreen === SUB_SCREENS.FLIGHT_RESULTS) {
      return (
        <FlightResultsScreen
          searchData={subScreenData}
          onBack={goBack}
          onSelectStrategy={(data) => navigateToSubScreen(SUB_SCREENS.PURCHASE_STRATEGIES, data)}
          cards={cards}
          benefitsData={benefitsData}
        />
      );
    }
    if (subScreen === SUB_SCREENS.PURCHASE_STRATEGIES) {
      return <PurchaseStrategiesScreen strategyData={subScreenData} onBack={goBack} />;
    }
    if (subScreen === SUB_SCREENS.TRANSFER_REFERENCE) {
      return <TransferReferenceScreen onBack={goBack} />;
    }
    if (showNearbyStores) {
      return <NearbyStores onSelectStore={handleStoreSelect} onBack={() => setShowNearbyStores(false)} />;
    }

    switch (activeTab) {
      case TABS.CARDS:
        return (
          <CardsTab
            cards={cards}
            onAddCard={() => setShowAddCard(true)}
            onDeleteCard={handleDeleteCard}
            onMerchantLookup={handleMerchantLookup}
            recommendations={recommendations}
            loading={loading}
            merchantData={merchantData}
            searchHistory={searchHistory}
            onHistoryItemClick={handleHistoryItemClick}
            isAuthenticated={isAuthenticated}
            benefitsData={benefitsData}
            benefitsLoading={benefitsLoading}
            benefitsError={benefitsError}
            onRefreshBenefits={() => { benefitsFetchedRef.current = false; loadBenefits(true); }}
          />
        );
      case TABS.FLIGHTS:
        return (
          <FlightsTab
            onSearchFlights={() => {}}
            onNavigateToResults={(screen, data) => navigateToSubScreen(screen, data)}
            cards={cards}
          />
        );
      case TABS.LOYALTY:
        return (
          <LoyaltyTab
            onNavigateToTransferReference={() => navigateToSubScreen(SUB_SCREENS.TRANSFER_REFERENCE)}
          />
        );
      case TABS.MORE:
        return (
          <MoreTab
            isAuthenticated={isAuthenticated}
            isAdmin={isAdmin}
            userEmail={getUserEmail()}
            onLogin={() => setShowAuth(true)}
            onLogout={handleLogout}
            onOpenAdmin={() => setShowAdmin(true)}
            onNavigateToTransferReference={() => navigateToSubScreen(SUB_SCREENS.TRANSFER_REFERENCE)}
            onNavigateToNearbyStores={() => setShowNearbyStores(true)}
          />
        );
      default:
        return null;
    }
  };

  return (
    <>
      <AppShell activeTab={activeTab} onTabChange={navigateToTab} userEmail={getUserEmail()}>
        {showMigrationPrompt && (
          <div className="mb-4 p-4 bg-warning-light rounded-2xl">
            <p className="text-sm text-on-surface mb-3">
              You have cards saved locally. Sync them to your account?
            </p>
            <div className="flex gap-2">
              <button
                onClick={handleMigrateCards}
                className="px-4 py-2 bg-primary text-on-primary rounded-full text-sm font-semibold"
              >
                Sync Cards
              </button>
              <button
                onClick={() => setShowMigrationPrompt(false)}
                className="px-4 py-2 bg-white text-on-surface-variant rounded-full text-sm font-medium border border-outline-variant"
              >
                Later
              </button>
            </div>
          </div>
        )}
        {renderContent()}
      </AppShell>

      {showAddCard && <AddCardModal onClose={() => setShowAddCard(false)} onAdd={handleAddCard} />}
      {showAuth && <AuthModal onClose={() => setShowAuth(false)} onAuthSuccess={handleAuthSuccess} />}
      {showAdmin && <AdminPanel onClose={() => setShowAdmin(false)} />}
    </>
  );
}

function App() {
  return (
    <AuthProvider>
      <LoyaltyProvider>
        <AppContent />
      </LoyaltyProvider>
    </AuthProvider>
  );
}

export default App;
